// src/components/createpost/PostActionSelector.js

import React, { useEffect, useMemo, useState } from 'react';
import {
  POST_ACTION_DESTINATION_OPTIONS,
  getPostActionDestination,
} from '../../constants/postActionDestinations';
import './PostActionSelector.css';

const MAX_LABEL_LENGTH = 40;

function PostActionSelector({
  isOpen,
  selectedAction,
  onSave,
  onClose,
  onClear,
}) {
  const [destinationKey, setDestinationKey] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setDestinationKey(selectedAction?.destinationKey || '');
    setLabel(selectedAction?.label || '');
    setError('');
  }, [isOpen, selectedAction]);

  const selectedDestination = useMemo(() => {
    return getPostActionDestination(destinationKey);
  }, [destinationKey]);

  if (!isOpen) {
    return null;
  }

  const handleDestinationChange = (event) => {
    setDestinationKey(event.target.value);
    setError('');
  };

  const handleLabelChange = (event) => {
    const nextLabel = event.target.value.slice(0, MAX_LABEL_LENGTH);

    setLabel(nextLabel);
    setError('');
  };

  const handleSave = () => {
    const trimmedLabel = label.trim();

    if (!selectedDestination) {
      setError('Select a destination.');
      return;
    }

    if (!trimmedLabel) {
      setError('Enter a button label.');
      return;
    }

    if (typeof onSave === 'function') {
      onSave({
        destinationKey: selectedDestination.key,
        label: trimmedLabel,
      });
    }
  };

  const handleClear = () => {
    setDestinationKey('');
    setLabel('');
    setError('');

    if (typeof onClear === 'function') {
      onClear();
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const handleOverlayClick = (event) => {
    if (
      event.target === event.currentTarget &&
      typeof onClose === 'function'
    ) {
      onClose();
    }
  };

  return (
    <div
      className="post-action-selector-overlay"
      role="presentation"
      onClick={handleOverlayClick}
    >
      <div
        className="post-action-selector-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="post-action-selector-title"
      >
        <div className="post-action-selector-handle" />

        <div className="post-action-selector-header">
          <div className="post-action-selector-heading">
            <h2 id="post-action-selector-title">Post Action</h2>

            <p>
              Add a button that opens a BarterFi destination.
            </p>
          </div>

          <button
            type="button"
            className="post-action-selector-close"
            onClick={onClose}
            aria-label="Close Post Action selector"
          >
            ×
          </button>
        </div>

        <div className="post-action-selector-content">
          <div className="post-action-selector-field">
            <label htmlFor="post-action-destination">
              Destination
            </label>

            <div className="post-action-selector-select-shell">
              <select
                id="post-action-destination"
                value={destinationKey}
                onChange={handleDestinationChange}
              >
                <option value="">Choose a destination</option>

                {POST_ACTION_DESTINATION_OPTIONS.map((destination) => (
                  <option
                    key={destination.key}
                    value={destination.key}
                  >
                    {destination.title}
                  </option>
                ))}
              </select>

              <span
                className="post-action-selector-select-arrow"
                aria-hidden="true"
              >
                ▼
              </span>
            </div>
          </div>

          <div className="post-action-selector-field">
            <div className="post-action-selector-label-row">
              <label htmlFor="post-action-label">
                Button label
              </label>

              <span>
                {label.length}/{MAX_LABEL_LENGTH}
              </span>
            </div>

            <input
              id="post-action-label"
              type="text"
              value={label}
              onChange={handleLabelChange}
              maxLength={MAX_LABEL_LENGTH}
              placeholder="Example: Try Now"
              autoComplete="off"
            />
          </div>

          {selectedDestination && label.trim() && (
            <div className="post-action-selector-preview">
              <span className="post-action-selector-preview-title">
                Preview
              </span>

              <button
                type="button"
                className="post-action-selector-preview-button"
                tabIndex={-1}
              >
                <span>{label.trim()}</span>

                <span
                  className="post-action-selector-preview-arrow"
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>

              <span className="post-action-selector-preview-note">
                Opens {selectedDestination.title}
              </span>
            </div>
          )}

          {error && (
            <p
              className="post-action-selector-error"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>

        <div className="post-action-selector-footer">
          {selectedAction && (
            <button
              type="button"
              className="post-action-selector-remove"
              onClick={handleClear}
            >
              Remove Action
            </button>
          )}

          <button
            type="button"
            className="post-action-selector-save"
            onClick={handleSave}
          >
            Save Action
          </button>
        </div>
      </div>
    </div>
  );
}

export default PostActionSelector;
